import { memo, useCallback, useMemo, useState } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { presentGive, presentScriptureReference } from "~/utils/events";
import { sharePage } from "~/utils/share";
import { track } from "~/utils/track";
import { urlFor } from "~/utils/url";
import { showShareButton } from "~/utils/features";
import { useLabels } from "~/hooks/useLabels";
import { useToasts } from "~/components/toast/useToasts";
import { Button } from "~/components/button/Button";
import { WordDefinitionModal } from "~/components/wordDefinitionModal/WordDefinitionModal";
import { RouteType } from "~/constants";
import { WatchViewSubview } from "~/components/watchView/WatchViewManager";
import { ShareModal } from "~/components/shareModal/ShareModal";
import { isMobileNative } from "~/utils/helpers";
import { getBibleReaderLink } from "~/utils/scripture";

import type { SyntheticEvent, ReactNode } from "react";
import type { SupportedBibleTranslation } from "~/constants";
import type { WordDefinitionData } from "~/components/wordDefinitionModal/WordDefinitionModal";
import type { ButtonProps } from "~/components/button/Button";
import type { SessionRouteLoaderData } from "./route";

export interface SlideLink {
  translation: SupportedBibleTranslation;
  type: "internal" | "external" | "scripture" | "word-definition";
  value: string | WordDefinitionData;
  text: string;
}

export interface SessionSlideLinksProps {
  currentTime: number;
  onRequestSubviewChange: (subview: WatchViewSubview) => void;
}

export const SessionSlideLinks = memo(function SessionSlideLinks({
  currentTime,
  onRequestSubviewChange,
}: SessionSlideLinksProps) {
  const { t } = useLabels();

  const { dismissAllToasts } = useToasts();

  const [isShareOpen, setIsShareOpen] = useState(false);

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const [wordDefinitionModalData, setWordDefinitionModalData] = useState<
    WordDefinitionData | undefined
  >(undefined);

  const shareUrl = urlFor(RouteType.SESSION, {
    class: data.session.class?.slug!,
    session: String(data.session.position),
  });

  const handleWordDefinitionModalClose = useCallback(() => {
    setWordDefinitionModalData(undefined);
  }, []);

  const handleWordDefinitionLinkClick = useCallback(
    (definition: WordDefinitionData) => {
      setWordDefinitionModalData(definition);
    },
    [],
  );

  const showShareModal = useCallback(() => {
    setIsShareOpen(true);
  }, []);

  const handleShareClose = useCallback(() => {
    setIsShareOpen(false);
  }, []);

  const currentSlideIndex = useMemo(
    () =>
      data.session.slides
        ?.filter((s) => s.timestamp <= (currentTime || 0))
        .map((s, index) => index)
        .reduce((a, b) => (a < b ? b : a)) || 0,
    [currentTime, data.session.slides],
  );

  const trackingData = useMemo(
    () => ({
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      trigger: "sessionSlideLinks",
    }),
    [data.session.class?.slug, data.session.position],
  );

  const slideLinks = useMemo<ButtonProps[]>(() => {
    // End of session
    if (currentSlideIndex + 1 === data.session.slides?.length) {
      return [
        {
          iconVariant: "brand",
          icon: "bibleproject",
          text: t("session.cta.give"),
          value: "give-button",
          onClick() {
            presentGive();
            track("crGive", trackingData);
          },
        },
        ...(showShareButton()
          ? [
              {
                icon: "arrow-up-from-square",
                text: t("session.cta.share"),
                value: "share-button",
                onClick(e: SyntheticEvent) {
                  if (isMobileNative()) {
                    sharePage(shareUrl, e.target);
                  } else {
                    showShareModal();
                  }

                  track("crSessionShare", trackingData);
                },
              },
            ]
          : []),
        {
          icon: "glasses-round",
          text: t("session.cta.review"),
          value: "review-button",
          onClick() {
            track("crReviewReflect", trackingData);
            onRequestSubviewChange(WatchViewSubview.Completed);
            dismissAllToasts();
          },
        },
      ];
    }

    const config = data.session.slides?.[currentSlideIndex].config ?? "{}";
    const linkData: Maybe<SlideLink[]> = JSON.parse(config).linkData;

    return (
      linkData?.map<ButtonProps>((l: SlideLink) => {
        if (l.type == "word-definition") {
          const value: WordDefinitionData = l.value as WordDefinitionData;
          const wordOriginal: ReactNode =
            value.language == "hebrew" ? (
              <bp-text-hebrew>{value.original}</bp-text-hebrew>
            ) : value.language == "greek" ? (
              <bp-text-greek>{value.original}</bp-text-greek>
            ) : (
              value.original
            );
          return {
            icon: "memo-pad",
            children: (
              <span>
                <em>{value.transliteration}</em>
                {` / `}
                {wordOriginal}
              </span>
            ),
            onClick() {
              handleWordDefinitionLinkClick(value);
            },
          };
        }

        const value: string = l.value as string;

        if (l.type === "external") {
          return {
            href: value,
            icon: "link",
            nativeBehavior: "presentLink",
            text: l.text,
            value: value,
          };
        } else {
          return isMobileNative()
            ? {
                icon: "book-open",
                value: value,
                text: l.text,
                onClick() {
                  presentScriptureReference(value, l.translation);
                },
              }
            : {
                href: getBibleReaderLink(value, l.translation),
                icon: "book-open",
                text: l.text,
                value: value,
              };
        }
      }) ?? []
    );
  }, [
    currentSlideIndex,
    data.session.slides,
    dismissAllToasts,
    handleWordDefinitionLinkClick,
    onRequestSubviewChange,
    shareUrl,
    showShareModal,
    t,
    trackingData,
  ]);

  return (
    <>
      {slideLinks.map((props) => {
        return (
          <Button
            key={props.value}
            appearance="tertiary"
            size="xs"
            {...props}
          />
        );
      })}

      <WordDefinitionModal
        isOpen={Boolean(wordDefinitionModalData)}
        definition={wordDefinitionModalData}
        onRequestClose={handleWordDefinitionModalClose}
      />
      <ShareModal
        isOpen={isShareOpen}
        shareUrl={shareUrl}
        onRequestClose={handleShareClose}
      />
    </>
  );
});
